import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { DenialListItem } from 'models/DenialListItem';
import { destroyDenialListItem } from 'services/api/denialListItems';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type DeleteDenialListEntryModalProps = {
  denialListItem: DenialListItem;
  onClose: () => void;
};

export default function DeleteDenialListEntryModal({ denialListItem, onClose }: DeleteDenialListEntryModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyDenialListItem,
  });

  const handleDelete = () => {
    mutate(
      { id: denialListItem.id },
      {
        onSuccess: () => {
          invalidateQueries({ denialListItems: true });
          addToast({ text: `${denialListItem.patientName} successfully removed from denial list` });
          onClose();
        },
      }
    );
  };

  return (
    <StandardModal title='Delete Entry?' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <StandardModal.PatientName>Patient: {denialListItem.patientName}</StandardModal.PatientName>
      <Content>
        Removing this patient takes them off your denial list, so new referrals that match will no longer be
        automatically flagged. This can be recovered by an administrator.
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleDelete} loading={isPending} disabled={isPending}>
          Delete Entry
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
